import { ReactNode, useEffect } from 'react'
import * as Sentry from '@sentry/nextjs'
import { DehydratedState } from '@tanstack/react-query'
import { NextPage } from 'next'
import { appWithTranslation } from 'next-i18next'
import NextApp, { AppContext, AppProps } from 'next/app'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { Router } from 'next/router'
import NProgress from 'nprogress'
import { SWRConfig } from 'swr'
import Toast from 'shared/components/toast'
import { FAVICON_LINK_KEYS } from 'shared/constants'
import { QueryClientProvider } from 'shared/contexts/query-client-context'
import { bootstrapFeatureFlags } from 'shared/feature-flags/bootstrap'
import { isFeatureFlagsDevToolsEnabled } from 'shared/feature-flags/env'
import { FeatureFlagsProvider } from 'shared/feature-flags/provider'
import { getCommunityLayout } from 'modules/community/components/community-layout'
import { defaultBrandSettings } from 'modules/white-label/constants/brand-settings'
import { IGNORED_WL_PATHS } from 'modules/white-label/constants/ignored-wl-paths'
import { BrandProvider } from 'modules/white-label/contexts/brand-context'
import { BrandSettingsInterface } from 'modules/white-label/types/brand-settings'
import { loadBrandSettingsServerSide } from 'modules/white-label/utils/load-brand-settings.server'
import NextI18nextConfig from '../../next-i18next.config'
import '../../styles/globals.css'
import '../../styles/cropper.css'
import 'nprogress/nprogress.css'
import '../../styles/nprogress.css'

bootstrapFeatureFlags()

const FeatureFlagsDevTools = isFeatureFlagsDevToolsEnabled()
  ? dynamic(
      () =>
        import('shared/feature-flags/dev-toolbar/feature-flags-dev-tools').then(
          module => module.FeatureFlagsDevTools,
        ),
      { ssr: false },
    )
  : (): null => null

export type NextPageWithLayout = NextPage & {
  getLayout: (page: ReactNode, props: AppProps['pageProps']) => ReactNode
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
  pageProps: AppProps['pageProps'] & {
    brand?: BrandSettingsInterface | null
    dehydratedState?: DehydratedState
  }
}

function App({ Component, pageProps }: AppPropsWithLayout): ReactNode {
  useEffect(() => {
    const handleRouteStart = (): NProgress.NProgress => NProgress.start()
    const handleRouteDone = (): NProgress.NProgress => NProgress.done()

    Router.events.on('routeChangeStart', handleRouteStart)
    Router.events.on('routeChangeComplete', handleRouteDone)
    Router.events.on('routeChangeError', handleRouteDone)

    return () => {
      // Make sure to remove the event handler on unmount!
      Router.events.off('routeChangeStart', handleRouteStart)
      Router.events.off('routeChangeComplete', handleRouteDone)
      Router.events.off('routeChangeError', handleRouteDone)
    }
  }, [])

  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout ?? getCommunityLayout

  const initialBrand = pageProps.brand ?? defaultBrandSettings
  const faviconPath = initialBrand?.favicon?.path ?? defaultBrandSettings.favicon?.path

  return (
    <>
      <Head>
        <link key={FAVICON_LINK_KEYS.icon} rel="icon" href={faviconPath} />
      </Head>
      <Sentry.ErrorBoundary>
        <BrandProvider initialBrand={initialBrand}>
          <FeatureFlagsProvider>
            <QueryClientProvider dehydratedState={pageProps.dehydratedState}>
              <SWRConfig
                value={{
                  revalidateOnFocus: false,
                  revalidateIfStale: false,
                  shouldRetryOnError: false,
                }}
              >
                {getLayout(<Component {...pageProps} />, pageProps)}
                <Toast />
              </SWRConfig>
            </QueryClientProvider>
            <FeatureFlagsDevTools />
          </FeatureFlagsProvider>
        </BrandProvider>
      </Sentry.ErrorBoundary>
    </>
  )
}

App.getInitialProps = async (appContext: AppContext) => {
  const appProps = await NextApp.getInitialProps(appContext)

  const requestUrl = appContext.ctx?.req?.url ?? null
  const isIgnoredPath = requestUrl
    ? IGNORED_WL_PATHS.some(path => requestUrl.startsWith(path))
    : false

  const brand = isIgnoredPath
    ? defaultBrandSettings
    : await loadBrandSettingsServerSide(appContext.ctx?.req)

  return {
    ...appProps,
    pageProps: {
      ...appProps.pageProps,
      brand,
    },
  }
}

// @ts-ignore
export default appWithTranslation(App, NextI18nextConfig)
